import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Building } from '@app/shared/models/building.interface';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableModule
} from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin, of, Subject } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DriverStaticFilterService } from '@app/driver-metrics/static/driver-static-filter.service';
import { AdmService } from '@services/adm.service';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { DriverStaticData } from '@app/driver-metrics/model/driver-static.interface';
import { SensorNodeService } from '@services/sensor-node.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-driver-metrics-static-table',
  imports: [
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTooltipModule,
    CommonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    NotificationBlockComponent
  ],
  templateUrl: './driver-metrics-static-table.component.html',
  styleUrl: './driver-metrics-static-table.component.scss'
})
export class DriverMetricsStaticTableComponent implements OnInit, OnDestroy {
  staticTableColumns = [
    'nodeId',
    'identificationNumber',
    'gtin',
    'responseCode',
    'firmwareVersion',
    'hardwareVersion',
    'additionalControlGearInformation',
    'luminaireYearOfManufacture',
    'luminaireWeekOfManufacture',
    'nominalInputPower',
    'powerAtMinimumDimLevel',
    'nominalMinimumACMainsVoltage',
    'nominalMaximumACMainsVoltage',
    'nominalLightOutput',
    'cri',
    'cct',
    'lightDistributionType',
    'luminaireColor',
    'luminaireIdentification',
    'version101VersionNumber',
    'version102VersionNumberOfAllIntegratedControlGear',
    'version103VersionNumberOfAllIntegratedControlDevices',
    'numberOfLogicalControlDeviceUnitsInTheBusUnit',
    'numberOfLogicalControlGearUnitsInTheBusUnit',
    'indexNumberOfThisLogicalControlGearUnit',
    'versionOfTheMemoryBank201',
    'guaranteedSupplyCurrentOfIntegratedDaliBusPowerSupplyInMa',
    'maximumSupplyCurrentOfIntegratedDaliBusPowerSupplyInMa',
    'versionOfTheMemoryBank202',
    'versionOfTheMemoryBank205',
    'versionOfTheMemoryBank206'
  ];

  @Input() building: Building;
  protected readonly StatusClass = StatusClass;

  data: DriverStaticData[] = [];
  isLoading = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly admService: AdmService,
    private readonly filterService: DriverStaticFilterService,
    private readonly nodeService: SensorNodeService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.filterService.filterCriteria$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadStaticData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStaticData(): void {
    this.isLoading = true;
    this.admService
      .getDriverStaticData(this.building, this.filterService.filterCriteriaValue)
      .pipe(
        takeUntil(this.destroy$),
        mergeMap((data) => {
          const nodes$ = this.nodeService.getNodesByIds(([...new Set(data.map((d) => d.nodeId))] as number[]).sort());
          return forkJoin([nodes$, of(data)]);
        })
      )
      .subscribe({
        next: ([nodes, data]) => {
          data.forEach((row) => {
            const node = nodes.find((n) => n.id === row.nodeId);
            row.sensorNodeLink = node ? `/buildings/${this.building.id}/${node.floorId}/sensor-nodes?n=${node.id}` : '';
          });
          this.data = data;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading driver static data:', error);
          this.isLoading = false;
        }
      });
  }

  navigateToUrl(url: string | null): void {
    if (url != null) {
      this.router.navigateByUrl(url);
    }
  }

  display(value: any, unit?: string): string {
    if (value == null) {
      return '-';
    }

    return unit ? `${value} ${unit}` : `${value}`;
  }
}
